import { useEffect, useRef, useState, type ReactNode } from 'react'
import { CITATION_LABELS, CITATION_STYLES, citationFor, type CitationFacts, type CitationStyle } from './citation'

/**
 * The two popovers the inspector opens: a permalink and a citation.
 *
 * Both exist because a citation must point at a region, not a page. The
 * permalink carries the bbox as data rather than as a rendering, so it
 * survives a re-ingest that renumbers nothing a reader can see.
 */

export function Popover({
  title,
  onClose,
  children,
  width = 360,
}: {
  title: string
  onClose: () => void
  children: ReactNode
  width?: number
}) {
  const panel = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation()
        onClose()
      }
    }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [onClose])

  useEffect(() => {
    panel.current?.focus()
  }, [])

  return (
    <>
      <div className="fixed inset-0 z-30" onClick={onClose} aria-hidden="true" />
      <div
        ref={panel}
        tabIndex={-1}
        role="dialog"
        aria-label={title}
        className="popover absolute z-40 flex flex-col gap-3 p-4"
        style={{ width, left: 16, right: 16, top: '100%', marginTop: 6, maxWidth: 'calc(100% - 32px)' }}
      >
        {children}
      </div>
    </>
  )
}

/** A value with a Copy beside it. The confirmation replaces the label, briefly. */
export function CopyButton({
  value,
  label = 'Copy link',
  primary = true,
}: {
  value: string
  label?: string
  primary?: boolean
}) {
  const [done, setDone] = useState(false)
  return (
    <button
      type="button"
      className={primary ? 'btn-primary py-1.5' : 'btn-secondary py-1.5'}
      onClick={() => {
        void navigator.clipboard?.writeText(value)
        setDone(true)
        window.setTimeout(() => setDone(false), 1600)
      }}
    >
      {done ? 'Copied' : label}
    </button>
  )
}

export function PermalinkPanel({
  permalink,
  iiif,
  onClose,
}: {
  permalink: string
  iiif: string | null
  onClose: () => void
}) {
  const origin = typeof window === 'undefined' ? '' : window.location.host
  const [path, query] = permalink.split('?')
  return (
    <Popover title="Permalink" onClose={onClose}>
      <div className="text-[15px] leading-normal">A citation must point at a region, not a page.</div>
      <div className="break-all rounded-[--radius-md] bg-surface px-2.5 py-2 font-mono text-[11.5px] leading-relaxed">
        {origin}
        {path}
        {query && <span className="text-accent">?{query}</span>}
      </div>
      <div className="grid grid-cols-[auto_1fr] gap-x-2.5 gap-y-1 font-mono text-[11px] leading-normal">
        <div className="text-ink2">resolves</div>
        <div>page, zoom, selection</div>
        <div className="text-ink2">stable</div>
        <div>re-ingest safe · bbox is data</div>
        <div className="text-ink2">guest</div>
        <div>metadata tier, no image</div>
      </div>
      <div className="flex gap-2">
        <CopyButton value={`${window.location.origin}${permalink}`} />
        {iiif && <CopyButton value={iiif} label="IIIF manifest" primary={false} />}
      </div>
    </Popover>
  )
}

export function CitePanel({
  facts,
  provided,
  onClose,
}: {
  facts: CitationFacts
  provided: Record<string, string | null> | null | undefined
  onClose: () => void
}) {
  const [style, setStyle] = useState<CitationStyle>('mla')
  const text = citationFor(style, provided, facts)
  return (
    <Popover title="Cite this region" onClose={onClose} width={400}>
      <div className="chrome">Citation</div>
      <div className="flex flex-wrap items-center gap-2 font-mono text-[10px] tracking-[0.06em]">
        {CITATION_STYLES.map((option) => (
          <button
            key={option}
            type="button"
            onClick={() => setStyle(option)}
            className={`uppercase ${option === style ? 'text-ink' : 'text-accent'}`}
            aria-pressed={option === style}
          >
            {CITATION_LABELS[option]}
          </button>
        ))}
      </div>
      <div className="max-h-56 overflow-y-auto whitespace-pre-wrap rounded-[--radius-md] bg-surface px-3 py-2.5 text-[13.5px] leading-normal">
        {text}
      </div>
      <div className="flex gap-2">
        <CopyButton value={text} label="Copy citation" />
      </div>
    </Popover>
  )
}
